import {
  isOutreachProgress,
  isOutreachSuppressed,
  type OutreachFirstTouchStatus,
  type OutreachIntent,
  type OutreachRow,
  type OutreachRowStatus,
} from '@discovr/types';

/**
 * The one stage the CRM's creator lists show per creator, highest priority
 * first: anything that ends the conversation, then anything a person must act
 * on, then how far the deal has got. A row matching several takes the first.
 */
export const CRM_STAGES = [
  'suppressed',
  'brand_declined',
  'closed',
  'escalated',
  'with_brand',
  'question_open',
  'rate_locked',
  'negotiating',
  'replied',
  'sent',
  'scheduled',
  'not_contacted',
] as const;
export type CrmStage = (typeof CRM_STAGES)[number];

export type CrmStageRow = Pick<
  OutreachRow,
  'status' | 'outreachState' | 'suppressed' | 'brandState' | 'escalatedAt' | 'lastReply' | 'lastMessage' | 'openQuoteIntent' | 'firstTouch'
>;

const NEGOTIATION_INTENTS: ReadonlySet<OutreachIntent> = new Set<OutreachIntent>(['quoted_rates', 'negotiating']);

/** Left the pipeline without a brand verdict; a brand rejection is its own stage. */
const CLOSED_ROSTER_STATUSES: ReadonlySet<OutreachRowStatus> = new Set<OutreachRowStatus>([
  'rejected_by_admin',
  'rejected_by_creator',
  'removed_by_admin',
]);

/** A first email on its way but not yet delivered: the CRM's "In flight". */
const IN_FLIGHT_FIRST_TOUCH: ReadonlySet<OutreachFirstTouchStatus> = new Set<OutreachFirstTouchStatus>([
  'scheduled',
  'queued',
  'sending',
  'held',
]);

const isInOutreach = (row: Pick<OutreachRow, 'status'>) => row.status === 'outreach';

const isSuppressed = (row: CrmStageRow) => row.suppressed || isOutreachSuppressed(row.outreachState);

const isBrandDeclined = (row: Pick<OutreachRow, 'status' | 'brandState'>) =>
  row.status === 'rejected_by_brand' || row.brandState === 'rejected';

const isClosed = (row: CrmStageRow) =>
  CLOSED_ROSTER_STATUSES.has(row.status) || (isInOutreach(row) && !isOutreachProgress(row.outreachState));

/** Every status past outreach that is not a rejection is with the brand, a status this build does not know included (owner ruling 2026-10-09). */
const isWithBrand = (row: CrmStageRow) => !isInOutreach(row);

const isNegotiationIntent = (intent: OutreachIntent | undefined) => intent !== undefined && NEGOTIATION_INTENTS.has(intent);

/** The creator's latest reply came after our latest send, so it is still waiting on us. */
function isReplyUnanswered(row: CrmStageRow): boolean {
  const reply = row.lastReply;
  if (!reply) return false;
  const lastSentAt = row.lastMessage?.sentAt;
  if (!lastSentAt) return true;
  return Date.parse(reply.receivedAt) > Date.parse(lastSentAt);
}

const hasOpenQuestion = (row: CrmStageRow) =>
  row.lastReply?.intent === 'question' && !row.lastReply.isAutoReply && isReplyUnanswered(row);

const isNegotiating = (row: CrmStageRow) =>
  row.outreachState === 'replied' && (isNegotiationIntent(row.openQuoteIntent) || isNegotiationIntent(row.lastReply?.intent));

const isFirstTouchInFlight = (row: CrmStageRow) =>
  row.outreachState === 'sourced' && row.firstTouch !== undefined && IN_FLIGHT_FIRST_TOUCH.has(row.firstTouch.status);

const STAGE_RULES: readonly (readonly [CrmStage, (row: CrmStageRow) => boolean])[] = [
  ['suppressed', isSuppressed],
  ['brand_declined', isBrandDeclined],
  ['closed', isClosed],
  ['escalated', (row) => Boolean(row.escalatedAt)],
  ['with_brand', isWithBrand],
  ['question_open', hasOpenQuestion],
  ['rate_locked', (row) => row.outreachState === 'rate_provided'],
  ['negotiating', isNegotiating],
  ['replied', (row) => row.outreachState === 'replied'],
  ['sent', (row) => row.outreachState === 'contacted'],
  ['scheduled', isFirstTouchInFlight],
];

export function crmStage(row: CrmStageRow): CrmStage {
  const match = STAGE_RULES.find(([, applies]) => applies(row));
  return match ? match[0] : 'not_contacted';
}

export type CrmRatesRow = Pick<OutreachRow, 'status' | 'outreachState' | 'brandState' | 'agreedDeliverables'>;

/** A rate is locked while outreach holds it, and stays locked with the brand until a rejection or removal ends the deal. */
const holdsLockedRate = (row: CrmRatesRow) =>
  isInOutreach(row) ? row.outreachState === 'rate_provided' : !isBrandDeclined(row) && !CLOSED_ROSTER_STATUSES.has(row.status);

/**
 * How many formats have a locked rate right now. The agreed deliverables stay
 * on the entry after a deal ends, so a closed or declined creator counts 0.
 */
export function lockedRateCount(row: CrmRatesRow): number {
  return holdsLockedRate(row) ? (row.agreedDeliverables?.length ?? 0) : 0;
}

/** The Creators list's Relationship column: the long view of a creator, coarser than the stage. */
export const CRM_RELATIONSHIPS = ['never_contacted', 'in_conversation', 'with_brand', 'rate_agreed', 'closed', 'suppressed'] as const;
export type CrmRelationship = (typeof CRM_RELATIONSHIPS)[number];

/** Exhaustive so a new stage fails to compile here until it is placed. A first email still on its way has not contacted anyone yet. */
const RELATIONSHIP_BY_STAGE: Record<CrmStage, CrmRelationship> = {
  suppressed: 'suppressed',
  brand_declined: 'closed',
  closed: 'closed',
  escalated: 'in_conversation',
  with_brand: 'with_brand',
  question_open: 'in_conversation',
  rate_locked: 'rate_agreed',
  negotiating: 'in_conversation',
  replied: 'in_conversation',
  sent: 'in_conversation',
  scheduled: 'never_contacted',
  not_contacted: 'never_contacted',
};

/** Needs-you flags say what to do next, not where the deal stands, so the long view reads past them. */
const ATTENTION_STAGES: ReadonlySet<CrmStage> = new Set<CrmStage>(['escalated', 'question_open']);

export function crmRelationship(row: CrmStageRow): CrmRelationship {
  const stage = crmStage(row);
  if (!ATTENTION_STAGES.has(stage)) return RELATIONSHIP_BY_STAGE[stage];
  const { escalatedAt: _escalatedAt, lastReply: _lastReply, ...rest } = row;
  return RELATIONSHIP_BY_STAGE[crmStage({ ...rest, escalatedAt: undefined, lastReply: undefined })];
}

/** The Creators list's Rates on file column: locked rates win over an open quote. */
export type CrmRatesOnFile = { kind: 'locked'; count: number } | { kind: 'quoted' } | { kind: 'none' };

export type CrmRatesOnFileRow = CrmRatesRow & Pick<OutreachRow, 'hasQuote'>;

/** An older Go that does not project has_quote reads as no quote rather than a guess from the reply intent. */
export function crmRatesOnFile(row: CrmRatesOnFileRow): CrmRatesOnFile {
  const count = lockedRateCount(row);
  if (count > 0) return { kind: 'locked', count };
  if (row.hasQuote === true) return { kind: 'quoted' };
  return { kind: 'none' };
}
